import type {
  AgentSuggestion,
  BuildFromSelectionsOutput,
  BuildFromSelectionsSlot,
  CategoryOptionsConversationContext,
  CategoryOptionsOutput,
  CommandResult,
  CommandStepOutput,
  ConversationTurn,
  DaySliderValues,
  ParseEmailOutput,
  PreferenceEmphasis,
  RecommendationCategoryWeights,
  WorkflowAdvanceOutput,
  WorkflowStartOutput,
  WorkflowStepDescriptor,
} from '@travel-planner/shared/schemas';
import type { ValueOf } from '@travel-planner/shared/types';
import type { AgentPanelContextType } from './types';
import { create } from 'zustand';
import { isPageActionResult } from './page-actions';
import { valueToEmphasis } from './preference-emphasis';

export const AGENT_PANEL_MODE = {
  Closed: 'closed',
  Closing: 'closing',
  Idle: 'idle',
  Typing: 'typing',
  Loading: 'loading',
  Recording: 'recording',
  Transcribing: 'transcribing',
  CommandSession: 'command-session',
  Generator: 'generator',
} as const;

// Duration of the slide-out animation. The store flips mode → 'closing',
// schedules the final flip → 'closed' after this many ms. Keep it in
// lockstep with the `slide-out` keyframe in agent-panel.module.scss.
export const AGENT_PANEL_CLOSE_DURATION_MS = 220;

export type CommandSessionPhase =
  | 'collecting'
  | 'confirm'
  | 'executing'
  | 'done'
  | 'cancelled';

export type CommandSession = {
  command: string | null;
  collected: Record<string, unknown>;
  missing: string[];
  phase: CommandSessionPhase;
  history: ConversationTurn[];
  pendingQuestion: string | null;
  /**
   * The kind of custom input widget to render for the next reply, looked up
   * via `getInputWidget()`. Undefined means render the default text input.
   */
  pendingInputWidget: string | null;
  confirmSummary: string | null;
  result: CommandResult | null;
  error: string | null;
};

// Mirrors the backend's WorkflowAdvanceOutput phases plus a frontend-only
// 'cancelled' for the local cancel button. The 'step-active' phase covers
// both `workflow-step` (open snapshot) and the duration of that step's
// commandSession; the store toggles `phase` to 'workflow-done' once the
// last step finishes so the UI can show the summary panel.
export type WorkflowSessionPhase =
  | 'step-active'
  | 'workflow-done'
  | 'workflow-cancelled';

// One step "visit" — picks accumulated during a single pass through the
// step. Looping a step (e.g. picking restaurants then activities under the
// same find_product step) appends a new visit; selections from earlier
// visits stay intact so the final summary shows everything.
export type WorkflowStepVisit = {
  // Which catalog step this visit is for (index into workflow.steps).
  stepIndex: number;
  // The category / sub-mode this visit was about, derived from the step's
  // collected params. Free-form so different command types can populate it
  // (e.g. find_product → category, future commands → whatever they want).
  // Used as a header in the trip summary.
  label: string | null;
  // Picks from this visit, in click order. Suggestions come straight from
  // the result panel, so they have thumbnails / city / price etc. ready
  // for the trip summary.
  selections: AgentSuggestion[];
};

export type WorkflowSession = {
  workflow: string;
  activeStepIndex: number;
  steps: ReadonlyArray<WorkflowStepDescriptor>;
  // Results of every non-skipped completed step, in command-execution order
  // (skipped steps are not represented here — see `skippedStepIndices`).
  completedResults: ReadonlyArray<CommandResult>;
  skippedStepIndices: ReadonlyArray<number>;
  // Per-visit selection log. Index 0 is the first visit; loops append.
  // Steps that don't produce selections (e.g. check_hotel_availability)
  // don't contribute visits. Read by the trip-summary card to render the
  // Picks block grouped by visit.
  visits: ReadonlyArray<WorkflowStepVisit>;
  phase: WorkflowSessionPhase;
};

const HISTORY_TAIL = 12;

const trimHistory = (history: ConversationTurn[]): ConversationTurn[] =>
  history.length > HISTORY_TAIL ? history.slice(-HISTORY_TAIL) : history;

// Translate a runner-returned step-open snapshot into a fresh CommandSession.
// Two shapes:
//  - `out.confirmSummary` is set → step is fully seeded + needs confirmation;
//    install commandSession in phase: 'confirm' so the panel renders the
//    confirm card directly. Without this, a fully-seeded workflow step
//    would sit in 'collecting' with an empty pendingQuestion forever.
//  - otherwise → install commandSession in phase: 'collecting' with the
//    runner's pre-computed nextQuestion (existing behavior).
const buildCommandSessionFromStepOpen = (
  out: WorkflowStartOutput
): CommandSession => {
  if (out.confirmSummary) {
    return {
      command: out.command,
      collected: { ...out.seedCollected },
      missing: [],
      phase: 'confirm',
      history: [{ role: 'assistant', content: out.confirmSummary }],
      pendingQuestion: null,
      pendingInputWidget: null,
      confirmSummary: out.confirmSummary,
      result: null,
      error: null,
    };
  }
  return {
    command: out.command,
    collected: { ...out.seedCollected },
    missing: out.missing,
    phase: 'collecting',
    history: out.nextQuestion
      ? [{ role: 'assistant', content: out.nextQuestion }]
      : [],
    pendingQuestion: out.nextQuestion || null,
    pendingInputWidget: null,
    confirmSummary: null,
    result: null,
    error: null,
  };
};

// Selection overlay for the `options` phase. Lives in Zustand (not the
// query cache) because it's UI interaction state, not a server snapshot.
// Seeded from `CategoryOptionsOutput['preselected']` on entry.
export type GeneratorSelections = {
  activityIds: string[];
  accommodationId: string | null;
  diningIds: string[];
  transportationIds: string[];
};

/** Unchecked product ids kept visible in compose until `buildFromSelections`. */
export type GeneratorStagedRemoval = {
  id: string;
  category: 'activities' | 'accommodation' | 'dining' | 'transportation';
};

// Per-day product overrides that replace the default round-robin allocation
// in `buildFromSelections.distribute`. Keyed by day-index-as-string to match
// the Zod schema (`z.string().regex(/^\d+$/)`).
export type DayOverride = {
  activityIds?: string[];
  accommodationId?: string | null;
  diningIds?: string[];
  transportationIds?: string[];
};

export type DayOverrides = Record<string, DayOverride>;

// Recommendation-engine state lives alongside dayOverrides because both
// share the same lifecycle: seeded on entry to the options phase, mutated
// by the recommendation panel, consumed by the buildFromSelections call.
// Default factory keeps the discriminated-union construction sites short.
export const DEFAULT_CATEGORY_WEIGHTS: RecommendationCategoryWeights = {
  stay: 'normal',
  activities: 'normal',
  food: 'normal',
  transportation: 'normal',
};

export type RecommendationState = {
  categoryWeights: RecommendationCategoryWeights;
  preferenceEmphasis: Record<string, PreferenceEmphasis>;
  /** Per-tag slider position (0–100). Independent per hashtag in the UI. */
  preferenceWeights: Record<string, number>;
  // Tracks how each 'high'-emphasis tag was added: 'brief' = AI-identified
  // from the client email (green chip); 'manual' = consultant added by hand
  // (outline chip). Persists through setRecommendationWeights round-trips.
  preferenceProvenance: Record<string, 'brief' | 'manual'>;
  dismissedPreferences: ReadonlyArray<string>;
  // Brief-gap card ids the consultant explicitly dismissed (the v3
  // gap UI; the docked gap count subtracts these.
  dismissedGaps: ReadonlyArray<string>;
  // Per-day category-weight overrides for the generator's options step,
  // keyed by `String(dayIndex)`. When a day appears here, that day's
  // ranking uses these multipliers instead of the brief-wide defaults.
  dayWeights: Record<string, DaySliderValues>;
  // Conversation context captured when the generator opened, so subsequent
  // `recommendationAnalysis` / "Re-run" calls can replay the same source
  // material. Without this, the analysis sees only the structured brief
  // and reports "0 emails · 0 chat messages".
  conversationContext: CategoryOptionsConversationContext | null;
};

export const DEFAULT_RECOMMENDATION_STATE: RecommendationState = {
  categoryWeights: DEFAULT_CATEGORY_WEIGHTS,
  preferenceEmphasis: {},
  preferenceWeights: {},
  preferenceProvenance: {},
  dismissedPreferences: [],
  dismissedGaps: [],
  dayWeights: {},
  conversationContext: null,
};

// The four product categories the generator's options step tabs across.
// Matches the `GeneratorCategory` union in `category-tab-bar.tsx`.
export type GeneratorActiveCategory =
  | 'transportation'
  | 'accommodation'
  | 'activities'
  | 'dining';

// v3 3-step build flow stepper (PRD §4.2 FR-10). Tracks which macro step
// the BuildFlowStepper highlights — orthogonal to the FSM `phase` which
// drives internal generator screens.
export type BuildStep = 'conversation' | 'day-picks' | 'send';

// One-way pipeline through the itinerary generator, reached from the
// sandbox surface (`/sandbox/agent-panel`); the inbox drawer hand-off into
// it was removed. Each phase carries the server snapshots it needs (held in
// the store, NOT in the query cache — `extracted`, `options`, and
// `schedule` are inputs to the next mutation).
export type GeneratorState =
  | {
      phase: 'options';
      extracted: ParseEmailOutput['extracted'];
      options: CategoryOptionsOutput['options'];
      preselected: CategoryOptionsOutput['preselected'];
      // UI-owned selection overlay (state-boundaries.md): the consultant's
      // active picks, seeded from `preselected` on entry. Held in Zustand
      // because it's interaction state, not server truth — the next
      // mutation (`itineraryBuildFromSelections`) reads it directly.
      selections: GeneratorSelections;
      /** Picks unchecked in compose but still shown until itinerary build. */
      stagedRemovals: ReadonlyArray<GeneratorStagedRemoval>;
      // Per-day product overrides. When set, `buildFromSelections.distribute`
      // uses these products for the specified days instead of the default
      // round-robin allocation.
      dayOverrides: DayOverrides;
      // Recommendation-engine state. Defaults are flat (no weights, no
      // dismissals, no cached analysis); the recommendation panel mutates
      // these via dedicated setters.
      recommendation: RecommendationState;
      // v3 Day-picks navigation state (PRD §4.2 FR-10). `activeDayIndex` is
      // the day the consultant is picking for; `activeCategory` is the
      // selected category tab. Held here so they survive panel close/reopen.
      activeDayIndex: number;
      activeCategory: GeneratorActiveCategory;
      // v3 3-step build flow. Defaults to 'day-picks' when entering options
      // from a thread; 'conversation' when Step 1 is shown. The
      // BuildFlowStepper reads this to highlight the active macro step.
      activeBuildStep: BuildStep;
    }
  | {
      // 'preview' renders the day-by-day schedule; 'budget' renders the
      // category breakdown. Both share the same payload because either
      // panel needs to compute totals, route Back to options with intact
      // picks, and forward to commit. The phase discriminator is the only
      // thing GeneratorPanel switches on.
      phase: 'preview' | 'budget';
      extracted: ParseEmailOutput['extracted'];
      // Carried from the options phase so the preview/budget panels can
      // (a) look up product details (price, title, currency, category) by
      // schedule-slot productId, and (b) restore Edit-picks navigation
      // back to options without losing the prior category bucket data.
      options: CategoryOptionsOutput['options'];
      preselected: CategoryOptionsOutput['preselected'];
      selections: GeneratorSelections;
      dayOverrides: DayOverrides;
      recommendation: RecommendationState;
      schedule: BuildFromSelectionsOutput['schedule'];
      versionId: BuildFromSelectionsOutput['versionId'];
      durationDays: BuildFromSelectionsOutput['durationDays'];
      estimatedTotal: BuildFromSelectionsOutput['estimatedTotal'];
      activeBuildStep: BuildStep;
    }
  | {
      phase: 'committing';
      total: number;
      succeeded: number;
      // The day the most recent successful add went into. Drives the
      // committing-step's "items added to Day {N}" line. `null` until the
      // first add lands.
      currentDayIndex: number | null;
      failed: ReadonlyArray<GeneratorFailedEntry>;
    }
  | {
      phase: 'partial-failure';
      total: number;
      succeeded: number;
      failed: ReadonlyArray<GeneratorFailedEntry>;
      // Required so "Retry remaining" can re-issue itineraryItems.create
      // against the same itinerary version.
      versionId: BuildFromSelectionsOutput['versionId'];
    }
  | { phase: 'done'; total: number };

// Flat representation of a single schedule slot, used by the commit
// pipeline. Carries the day/slot/time/product so the create call has
// everything it needs and the partial-failure UI can label the row
// without a productIndex.
export type GeneratorScheduleEntry = {
  dayIndex: number;
  slot: BuildFromSelectionsSlot;
  startTime: string;
  productId: string;
};

// One row of the partial-failure list. `code` is the TRPC error code
// (e.g. 'PRECONDITION_FAILED') if one was extractable; `reason` is the
// surfaceable error message.
export type GeneratorFailedEntry = {
  entry: GeneratorScheduleEntry;
  code: string | null;
  reason: string;
};

export type AgentPanelMode = ValueOf<typeof AGENT_PANEL_MODE>;

// Persistent status surfaced just above the input by `<AgentPanel>`,
// reflecting the result of the most-recent page-action call (currently
// only `apply_filters` on /products). Lives across new turns and panel
// reopens so the consultant can always see what's currently applied;
// cleared by `reset()` (the trash button) or by the page itself when
// the function it represents stops being relevant.
export type PageActionStatus = {
  // The CommandResult.kind that produced this status, e.g. 'apply_filters'.
  // Pages that want to render their own visual key off this.
  kind: string;
  // Plain string from the result.humanSummary — already user-friendly
  // ("Applied: Bakery, in Lisbon"). The chip renderer can post-process
  // it, but the default treatment just shows it verbatim.
  summary: string;
};

// Pending cross-page navigation handoff. Set when the planner returns
// `phase: 'navigate'` (the consultant asked for an action that lives on a
// different page). Drives the in-panel confirm card. On confirm the panel
// stashes a `PendingHandoffReplay` and navigates; on cancel the slot is
// cleared with no side effects. See the per-page-scoping PRD.
export type PendingNavigation = {
  target: AgentPanelContextType;
  reason: string;
  replayUtterance: string;
};

// One-shot replay slot consumed by the next adapter mount whose
// `contextType` matches `target`. Atomic consume guarantees the replay
// fires exactly once even with React 19 StrictMode double-mount.
export type PendingHandoffReplay = {
  target: AgentPanelContextType;
  utterance: string;
};

export interface AgentPanelState {
  mode: AgentPanelMode;
  query: string;
  commandSession: CommandSession | null;
  workflowSession: WorkflowSession | null;
  // Set briefly when the planner returns `workflow-requested` so the
  // useAgentCommand hook can immediately fire `agentPanel.workflowStart`.
  // Cleared as soon as the workflow opens its first step.
  pendingWorkflowName: string | null;
  // Itinerary generator pipeline state. `null` whenever `mode` is not
  // `Generator`. Holds extracted/options/schedule snapshots between server
  // calls — these are intermediate inputs to the next mutation, not query
  // cache entries.
  generatorState: GeneratorState | null;
  micDenied: boolean;
  voiceDisclosureAcknowledged: boolean;
  // Status of the last page-action result the panel saw — see
  // PageActionStatus. Survives close/reopen, cleared by reset().
  lastPageAction: PageActionStatus | null;
  // Pending cross-page handoff awaiting consultant confirmation. Drives the
  // in-panel `NavigateHandoffCard`. Survives close/reopen so a closed-mid-
  // confirm panel reopens to the same card. Cleared by `reset()`,
  // `cancelCommand()`, or by confirming the handoff (which transfers the
  // utterance to `pendingHandoffReplay`).
  pendingNavigation: PendingNavigation | null;
  // One-shot replay slot — the consultant confirmed a handoff and the panel
  // is mid-navigation. The next adapter mount whose `contextType` matches
  // `target` consumes the slot atomically and replays the utterance via
  // `startSession`. Cleared by `reset()`.
  pendingHandoffReplay: PendingHandoffReplay | null;

  open: () => void;
  close: () => void;
  /**
   * Snap the panel back to the empty-state idle mode without closing it:
   * clear the query, drop any active command/workflow session, and reset
   * `pendingWorkflowName`. Voice's own state lives in the hook (not the
   * store) so the caller is expected to call `voice.cancel()` /
   * `voice.clearError()` alongside this if needed.
   */
  reset: () => void;
  setMode: (mode: AgentPanelMode) => void;
  setQuery: (query: string) => void;
  setGeneratorState: (next: GeneratorState | null) => void;
  // Per-day product overrides for the itinerary generator.
  setDayOverride: (dayIndex: number, override: DayOverride) => void;
  removeDayOverride: (dayIndex: number) => void;
  // Recommendation-engine setters. Operate on the active `recommendation`
  // slot inside the current `options`/`preview`/`budget` phase; no-op
  // otherwise. All five setters merge instead of replacing so the panel can
  // tweak one knob at a time without resetting siblings.
  setRecommendationWeights: (
    weights: Partial<RecommendationCategoryWeights>
  ) => void;
  setPreferenceEmphasis: (
    preferenceId: string,
    emphasis: PreferenceEmphasis
  ) => void;
  /** Sets per-tag slider weight (0–100) and derived emphasis for re-rank. */
  setHashtagWeight: (preferenceId: string, weight: number) => void;
  /** Removes a hashtag from the active steering set (tuning panel ✕). */
  clearPreferenceEmphasis: (preferenceId: string) => void;
  dismissPreference: (preferenceId: string) => void;
  // Dismiss a brief-gap card by id (v3 gap UI). No-op when already
  // dismissed. Operates on the options-phase recommendation slot.
  dismissGap: (gapId: string) => void;
  // v3 Day-picks navigation setters. No-op outside the options phase.
  setActiveDayIndex: (dayIndex: number) => void;
  setActiveCategory: (category: GeneratorActiveCategory) => void;
  // v3 3-step build flow stepper. Updates `activeBuildStep` on the active
  // generator phase (options / preview / budget). No-op when not generating.
  setActiveBuildStep: (step: BuildStep) => void;
  // Commits per-day category-weight overrides for the generator's options
  // step, keyed by `String(dayIndex)`. Passing `null` clears the entry so
  // that day falls back to the brief-wide defaults.
  setDayWeights: (dayIndex: number, values: DaySliderValues | null) => void;
  resetRecommendationOverrides: () => void;
  setMicDenied: (value: boolean) => void;
  acknowledgeVoiceDisclosure: () => void;
  startCommandSession: (utterance: string) => void;
  appendUserTurn: (utterance: string) => void;
  // Accepts the full discriminated CommandStepOutput so new phases (e.g.
  // 'workflow-requested') flow in without widening the call sites. The
  // 'collecting' variant on CommandStepOutput already carries the optional
  // `inputWidget` discriminator, which is forwarded to `pendingInputWidget`
  // when this action runs.
  applyServerStep: (step: CommandStepOutput) => void;
  markExecuting: () => void;
  setCommandError: (message: string | null) => void;
  cancelCommand: () => void;

  // Workflow-layer setters.
  applyWorkflowStart: (out: WorkflowStartOutput) => void;
  applyWorkflowAdvance: (out: WorkflowAdvanceOutput) => void;
  // Append a finished step's result to the local accumulator. Called by the
  // hook just before `workflowAdvance` so the next-call payload has the
  // up-to-date `completedResults`.
  appendWorkflowStepResult: (result: CommandResult) => void;
  // Record that the active step was skipped. Called by the hook just before
  // `workflowAdvance({kind: 'skip'})`.
  recordWorkflowSkip: (stepIndex: number) => void;
  // Toggle a product into / out of the active step's selection set. The
  // suggestion is stashed verbatim (thumbnail, city, etc.) so the trip
  // summary doesn't have to refetch anything later.
  toggleWorkflowSelection: (suggestion: AgentSuggestion) => void;
  // Reset the active step's commandSession back to phase=collecting with a
  // fresh seed (re-runs the runner's `beforeAsk` so the chip picker
  // reappears). Selections from prior visits stay intact. Used by the
  // "Pick another category" loop button.
  restartCurrentWorkflowStep: (
    seedCollected: Record<string, unknown>,
    nextQuestion: string,
    missing: string[]
  ) => void;
  cancelWorkflow: () => void;
  // Cleared by the hook once it has consumed the pending workflow name.
  clearPendingWorkflow: () => void;

  // Page-action status setters.
  setLastPageAction: (status: PageActionStatus | null) => void;

  // Navigation-handoff setters. See `handle-navigate-handoff.ts` for the
  // full handoff lifecycle.
  setPendingNavigation: (next: PendingNavigation | null) => void;
  setPendingHandoffReplay: (next: PendingHandoffReplay | null) => void;
  /**
   * Atomic get-and-clear: returns the pending utterance if the slot's
   * `target` matches `forContextType`, then clears the slot. Otherwise
   * returns `null` and leaves the slot intact. Atomicity guards against
   * React 19 StrictMode double-firing the replay effect.
   */
  consumePendingHandoffReplay: (
    forContextType: AgentPanelContextType
  ) => string | null;
}

export const useAgentPanelStore = create<AgentPanelState>()((set, get) => ({
  mode: AGENT_PANEL_MODE.Closed,
  query: '',
  commandSession: null,
  workflowSession: null,
  pendingWorkflowName: null,
  generatorState: null,
  micDenied: false,
  voiceDisclosureAcknowledged: false,
  lastPageAction: null,
  pendingNavigation: null,
  pendingHandoffReplay: null,

  open: () =>
    set((state) => {
      if (
        state.mode !== AGENT_PANEL_MODE.Closed &&
        state.mode !== AGENT_PANEL_MODE.Closing
      ) {
        return state;
      }
      // Re-opening with a preserved session — drop straight back into the
      // CommandSession mode so the conversation log + last result render
      // immediately. Without this, mode resets to Idle and the persisted
      // history would be hidden behind the empty-state capabilities list.
      if (state.commandSession) {
        return { mode: AGENT_PANEL_MODE.CommandSession };
      }
      return { mode: AGENT_PANEL_MODE.Idle };
    }),

  close: () => {
    const state = get();
    // Already closed/closing — no-op so a second call doesn't re-arm the
    // timeout or re-trigger the animation.
    if (
      state.mode === AGENT_PANEL_MODE.Closed ||
      state.mode === AGENT_PANEL_MODE.Closing
    ) {
      return;
    }
    // Flip to 'closing' so the panel renders the slide-out animation.
    // Session state (command session, generator state, page-action chip)
    // is preserved across the close so re-opening shows what was there
    // before. The explicit "trash" button in the header (and `reset()`)
    // is the way to wipe.
    set({ mode: AGENT_PANEL_MODE.Closing });
    // Schedule the final unmount. We don't track the timeout id — if a
    // caller re-opens the panel mid-animation, the open() action flips
    // mode to Idle and the stale timeout fires harmlessly (it sets mode
    // to Closed only when still in Closing).
    window.setTimeout(() => {
      const current = get();
      if (current.mode === AGENT_PANEL_MODE.Closing) {
        set({ mode: AGENT_PANEL_MODE.Closed });
      }
    }, AGENT_PANEL_CLOSE_DURATION_MS);
  },

  reset: () =>
    set((state) => ({
      mode: AGENT_PANEL_MODE.Idle,
      query: '',
      commandSession: null,
      workflowSession: null,
      pendingWorkflowName: null,
      lastPageAction: null,
      generatorState: null,
      pendingNavigation: null,
      pendingHandoffReplay: null,
      micDenied: state.micDenied,
      voiceDisclosureAcknowledged: state.voiceDisclosureAcknowledged,
    })),

  setMode: (mode) => set((state) => (state.mode === mode ? state : { mode })),

  setQuery: (query) => set({ query }),

  setGeneratorState: (next) => set({ generatorState: next }),

  setDayOverride: (dayIndex, override) =>
    set((state) => {
      const gs = state.generatorState;
      if (
        !gs ||
        (gs.phase !== 'options' &&
          gs.phase !== 'preview' &&
          gs.phase !== 'budget')
      )
        return state;
      return {
        generatorState: {
          ...gs,
          dayOverrides: { ...gs.dayOverrides, [String(dayIndex)]: override },
        },
      };
    }),

  removeDayOverride: (dayIndex) =>
    set((state) => {
      const gs = state.generatorState;
      if (
        !gs ||
        (gs.phase !== 'options' &&
          gs.phase !== 'preview' &&
          gs.phase !== 'budget')
      )
        return state;
      const { [String(dayIndex)]: _, ...rest } = gs.dayOverrides;
      return {
        generatorState: { ...gs, dayOverrides: rest },
      };
    }),

  setRecommendationWeights: (weights) =>
    set((state) => {
      const gs = state.generatorState;
      if (
        !gs ||
        (gs.phase !== 'options' &&
          gs.phase !== 'preview' &&
          gs.phase !== 'budget')
      )
        return state;
      return {
        generatorState: {
          ...gs,
          recommendation: {
            ...gs.recommendation,
            categoryWeights: {
              ...gs.recommendation.categoryWeights,
              ...weights,
            },
          },
        },
      };
    }),

  setPreferenceEmphasis: (preferenceId, emphasis) =>
    set((state) => {
      const gs = state.generatorState;
      if (
        !gs ||
        (gs.phase !== 'options' &&
          gs.phase !== 'preview' &&
          gs.phase !== 'budget')
      )
        return state;
      return {
        generatorState: {
          ...gs,
          recommendation: {
            ...gs.recommendation,
            preferenceEmphasis: {
              ...gs.recommendation.preferenceEmphasis,
              [preferenceId]: emphasis,
            },
            // Stamp 'manual' only for newly added tags. Brief-sourced
            // provenance from openFromThread must survive weight tweaks.
            preferenceProvenance:
              preferenceId in gs.recommendation.preferenceProvenance
                ? gs.recommendation.preferenceProvenance
                : {
                    ...gs.recommendation.preferenceProvenance,
                    [preferenceId]: 'manual',
                  },
          },
        },
      };
    }),

  setHashtagWeight: (preferenceId, weight) =>
    set((state) => {
      const gs = state.generatorState;
      if (
        !gs ||
        (gs.phase !== 'options' &&
          gs.phase !== 'preview' &&
          gs.phase !== 'budget')
      )
        return state;
      const clamped = Math.max(0, Math.min(100, Math.round(weight)));
      const emphasis = valueToEmphasis(clamped);
      return {
        generatorState: {
          ...gs,
          recommendation: {
            ...gs.recommendation,
            preferenceEmphasis: {
              ...gs.recommendation.preferenceEmphasis,
              [preferenceId]: emphasis,
            },
            preferenceWeights: {
              ...gs.recommendation.preferenceWeights,
              [preferenceId]: clamped,
            },
            preferenceProvenance:
              preferenceId in gs.recommendation.preferenceProvenance
                ? gs.recommendation.preferenceProvenance
                : {
                    ...gs.recommendation.preferenceProvenance,
                    [preferenceId]: 'manual',
                  },
          },
        },
      };
    }),

  clearPreferenceEmphasis: (preferenceId) =>
    set((state) => {
      const gs = state.generatorState;
      if (
        !gs ||
        (gs.phase !== 'options' &&
          gs.phase !== 'preview' &&
          gs.phase !== 'budget')
      )
        return state;
      const { [preferenceId]: _emphasis, ...preferenceEmphasis } =
        gs.recommendation.preferenceEmphasis;
      const { [preferenceId]: _weight, ...preferenceWeights } =
        gs.recommendation.preferenceWeights;
      const { [preferenceId]: _provenance, ...preferenceProvenance } =
        gs.recommendation.preferenceProvenance;
      return {
        generatorState: {
          ...gs,
          recommendation: {
            ...gs.recommendation,
            preferenceEmphasis,
            preferenceWeights,
            preferenceProvenance,
          },
        },
      };
    }),

  dismissPreference: (preferenceId) =>
    set((state) => {
      const gs = state.generatorState;
      if (
        !gs ||
        (gs.phase !== 'options' &&
          gs.phase !== 'preview' &&
          gs.phase !== 'budget')
      )
        return state;
      if (gs.recommendation.dismissedPreferences.includes(preferenceId))
        return state;
      return {
        generatorState: {
          ...gs,
          recommendation: {
            ...gs.recommendation,
            dismissedPreferences: [
              ...gs.recommendation.dismissedPreferences,
              preferenceId,
            ],
          },
        },
      };
    }),

  dismissGap: (gapId) =>
    set((state) => {
      const gs = state.generatorState;
      if (
        !gs ||
        (gs.phase !== 'options' &&
          gs.phase !== 'preview' &&
          gs.phase !== 'budget')
      )
        return state;
      if (gs.recommendation.dismissedGaps.includes(gapId)) return state;
      return {
        generatorState: {
          ...gs,
          recommendation: {
            ...gs.recommendation,
            dismissedGaps: [...gs.recommendation.dismissedGaps, gapId],
          },
        },
      };
    }),

  setActiveDayIndex: (dayIndex) =>
    set((state) => {
      const gs = state.generatorState;
      if (!gs || gs.phase !== 'options') return state;
      if (gs.activeDayIndex === dayIndex) return state;
      return { generatorState: { ...gs, activeDayIndex: dayIndex } };
    }),

  setActiveCategory: (category) =>
    set((state) => {
      const gs = state.generatorState;
      if (!gs || gs.phase !== 'options') return state;
      if (gs.activeCategory === category) return state;
      return { generatorState: { ...gs, activeCategory: category } };
    }),

  setActiveBuildStep: (step) =>
    set((state) => {
      const gs = state.generatorState;
      if (!gs) return state;
      if (
        gs.phase !== 'options' &&
        gs.phase !== 'preview' &&
        gs.phase !== 'budget'
      )
        return state;
      if (gs.activeBuildStep === step) return state;
      return { generatorState: { ...gs, activeBuildStep: step } };
    }),

  setDayWeights: (dayIndex, values) =>
    set((state) => {
      const gs = state.generatorState;
      if (
        !gs ||
        (gs.phase !== 'options' &&
          gs.phase !== 'preview' &&
          gs.phase !== 'budget')
      )
        return state;
      const key = String(dayIndex);
      if (values === null) {
        const { [key]: _, ...rest } = gs.recommendation.dayWeights;
        return {
          generatorState: {
            ...gs,
            recommendation: { ...gs.recommendation, dayWeights: rest },
          },
        };
      }
      return {
        generatorState: {
          ...gs,
          recommendation: {
            ...gs.recommendation,
            dayWeights: { ...gs.recommendation.dayWeights, [key]: values },
          },
        },
      };
    }),

  resetRecommendationOverrides: () =>
    set((state) => {
      const gs = state.generatorState;
      if (
        !gs ||
        (gs.phase !== 'options' &&
          gs.phase !== 'preview' &&
          gs.phase !== 'budget')
      )
        return state;
      return {
        generatorState: {
          ...gs,
          recommendation: {
            ...DEFAULT_RECOMMENDATION_STATE,
            // Preserve the captured conversation context so the analysis
            // query keeps a stable cache key — only the user-tunable knobs
            // reset. The analysis result itself lives in the query cache.
            conversationContext: gs.recommendation.conversationContext,
          },
        },
      };
    }),

  setMicDenied: (micDenied) => set({ micDenied }),

  acknowledgeVoiceDisclosure: () => set({ voiceDisclosureAcknowledged: true }),

  startCommandSession: (utterance) =>
    set((state) => {
      // Carry display history across a fresh prompt so the conversation
      // log stays continuous when the consultant follows up after a
      // completed command (e.g. applied a filter, then asks for another).
      // The previous session's collected params / phase / result are
      // intentionally NOT preserved — this is a new ask. The server-side
      // snapshot in `useAgentCommand.startSession` builds its own
      // utterance-only history, so the planner doesn't see the prior
      // turns and won't be confused by them.
      //
      // Explicitly wiping the conversation is a separate user action
      // (the trash button in the panel header → store.reset()).
      const priorHistory = state.commandSession?.history ?? [];
      return {
        mode: AGENT_PANEL_MODE.CommandSession,
        commandSession: {
          command: null,
          collected: {},
          missing: [],
          phase: 'collecting',
          history: trimHistory([
            ...priorHistory,
            { role: 'user', content: utterance },
          ]),
          pendingQuestion: null,
          pendingInputWidget: null,
          confirmSummary: null,
          result: null,
          error: null,
        },
      };
    }),

  appendUserTurn: (utterance) =>
    set((state) =>
      state.commandSession
        ? {
            commandSession: {
              ...state.commandSession,
              history: trimHistory([
                ...state.commandSession.history,
                { role: 'user', content: utterance },
              ]),
              error: null,
            },
          }
        : state
    ),

  applyServerStep: (step) =>
    set((state) => {
      const prior = state.commandSession ?? {
        command: null,
        collected: {},
        missing: [],
        phase: 'collecting' as CommandSessionPhase,
        history: [],
        pendingQuestion: null,
        pendingInputWidget: null,
        confirmSummary: null,
        result: null,
        error: null,
      };
      if (step.phase === 'collecting') {
        return {
          mode: AGENT_PANEL_MODE.CommandSession,
          commandSession: {
            ...prior,
            command: step.command || prior.command,
            collected: step.collected,
            missing: step.missing,
            phase: 'collecting',
            pendingQuestion: step.nextQuestion,
            pendingInputWidget: step.inputWidget ?? null,
            confirmSummary: null,
            result: null,
            history: trimHistory([
              ...prior.history,
              { role: 'assistant', content: step.nextQuestion },
            ]),
          },
        };
      }
      if (step.phase === 'confirm') {
        return {
          mode: AGENT_PANEL_MODE.CommandSession,
          commandSession: {
            ...prior,
            command: step.command,
            collected: step.collected,
            missing: [],
            phase: 'confirm',
            pendingQuestion: null,
            pendingInputWidget: null,
            confirmSummary: step.summary,
            result: null,
            history: trimHistory([
              ...prior.history,
              { role: 'assistant', content: step.summary },
            ]),
          },
        };
      }
      if (step.phase === 'done') {
        // Page-action results (apply_filters etc.) already have a
        // dedicated in-panel chip renderer that shows the human summary,
        // so appending the same string to the conversation history would
        // double-render it. Skip the history append for those kinds; the
        // renderer chip is the single source of truth for the visible
        // summary. Non-page-action kinds (operation_list, message_sent,
        // etc.) keep their plain-text history bubble as before.
        const isPageAction = isPageActionResult(step.result);
        return {
          mode: AGENT_PANEL_MODE.CommandSession,
          commandSession: {
            ...prior,
            command: step.command,
            phase: 'done',
            pendingQuestion: null,
            pendingInputWidget: null,
            result: step.result,
            history: isPageAction
              ? prior.history
              : trimHistory([
                  ...prior.history,
                  { role: 'assistant', content: step.result.humanSummary },
                ]),
          },
        };
      }
      if (step.phase === 'workflow-requested') {
        // The planner picked a workflow. Surface the requested name so the
        // hook can fire `agentPanel.workflowStart`. Don't touch the active
        // commandSession yet — the workflow's first step will install a
        // fresh one via `applyWorkflowStart`.
        return {
          mode: AGENT_PANEL_MODE.CommandSession,
          pendingWorkflowName: step.workflow,
        };
      }
      if (step.phase === 'navigate') {
        // The planner picked the cross-page navigation handoff. Stash the
        // proposed handoff so the panel renders the confirm card. Drop the
        // active commandSession — its prior turns reference the wrong
        // page's tool catalog and would confuse the LLM if reused. The
        // confirm card is a sibling slot, not a session phase.
        return {
          mode: AGENT_PANEL_MODE.CommandSession,
          commandSession: null,
          pendingNavigation: {
            target: step.target,
            reason: step.reason,
            replayUtterance: step.replayUtterance,
          },
        };
      }
      // cancelled
      return {
        mode: AGENT_PANEL_MODE.CommandSession,
        commandSession: {
          ...prior,
          phase: 'cancelled',
          pendingQuestion: null,
          pendingInputWidget: null,
          confirmSummary: null,
        },
      };
    }),

  markExecuting: () =>
    set((state) =>
      state.commandSession
        ? {
            commandSession: { ...state.commandSession, phase: 'executing' },
          }
        : state
    ),

  setCommandError: (message) =>
    set((state) =>
      state.commandSession
        ? {
            commandSession: { ...state.commandSession, error: message },
          }
        : state
    ),

  cancelCommand: () =>
    set((state) =>
      state.commandSession
        ? {
            commandSession: {
              ...state.commandSession,
              phase: 'cancelled',
              pendingQuestion: null,
              pendingInputWidget: null,
              confirmSummary: null,
            },
          }
        : state
    ),

  // Open the first step of a workflow. Resets `commandSession` to a fresh
  // collecting snapshot seeded with the runner's `seedCollected` AND the
  // pre-computed first question. Seeding the question + assistant history
  // turn lets the consultant start typing immediately — no LLM round-trip
  // needed just to learn what to ask.
  applyWorkflowStart: (out) =>
    set({
      mode: AGENT_PANEL_MODE.CommandSession,
      pendingWorkflowName: null,
      workflowSession: {
        workflow: out.workflow,
        activeStepIndex: out.activeStepIndex,
        steps: out.steps,
        completedResults: [],
        skippedStepIndices: [],
        visits: [],
        phase: 'step-active',
      },
      commandSession: buildCommandSessionFromStepOpen(out),
    }),

  // Apply the runner's `workflowAdvance` response. Three phases:
  //  - 'workflow-step'  → install the next step (fresh commandSession + bumped index)
  //  - 'workflow-done'  → mark workflowSession done; commandSession stays as the last step's result
  //  - 'workflow-cancelled' → mark cancelled
  // The caller (`useAgentCommand`) is responsible for passing the updated
  // `completedResults` and `skippedStepIndices` to the runner BEFORE calling
  // this. The store reflects whatever the runner returned.
  applyWorkflowAdvance: (out) =>
    set((state) => {
      const ws = state.workflowSession;
      if (!ws) return state;
      if (out.phase === 'workflow-step') {
        return {
          mode: AGENT_PANEL_MODE.CommandSession,
          workflowSession: {
            ...ws,
            // Server-truth: trust the runner for index, steps, and the
            // catalog. The frontend keeps its local
            // completedResults/skippedStepIndices accumulators in sync via
            // separate setters (`appendWorkflowStepResult`, `recordSkip`).
            workflow: out.workflow,
            activeStepIndex: out.activeStepIndex,
            steps: out.steps,
            phase: 'step-active',
          },
          commandSession: buildCommandSessionFromStepOpen(out),
        };
      }
      if (out.phase === 'workflow-done') {
        return {
          workflowSession: {
            ...ws,
            phase: 'workflow-done',
            completedResults: out.completedResults,
            steps: out.steps,
          },
        };
      }
      // workflow-cancelled
      return {
        workflowSession: { ...ws, phase: 'workflow-cancelled' },
      };
    }),

  appendWorkflowStepResult: (result) =>
    set((state) =>
      state.workflowSession
        ? {
            workflowSession: {
              ...state.workflowSession,
              completedResults: [
                ...state.workflowSession.completedResults,
                result,
              ],
            },
          }
        : state
    ),

  recordWorkflowSkip: (stepIndex) =>
    set((state) =>
      state.workflowSession
        ? {
            workflowSession: {
              ...state.workflowSession,
              skippedStepIndices: [
                ...state.workflowSession.skippedStepIndices,
                stepIndex,
              ],
            },
          }
        : state
    ),

  cancelWorkflow: () =>
    set((state) =>
      state.workflowSession
        ? {
            workflowSession: {
              ...state.workflowSession,
              phase: 'workflow-cancelled',
            },
            commandSession: state.commandSession
              ? {
                  ...state.commandSession,
                  phase: 'cancelled',
                  pendingQuestion: null,
                  pendingInputWidget: null,
                  confirmSummary: null,
                }
              : state.commandSession,
          }
        : state
    ),

  clearPendingWorkflow: () =>
    set((state) =>
      state.pendingWorkflowName === null ? state : { pendingWorkflowName: null }
    ),

  setLastPageAction: (status) => set({ lastPageAction: status }),

  setPendingNavigation: (next) => set({ pendingNavigation: next }),

  setPendingHandoffReplay: (next) => set({ pendingHandoffReplay: next }),

  consumePendingHandoffReplay: (forContextType) => {
    const replay = get().pendingHandoffReplay;
    if (!replay || replay.target !== forContextType) return null;
    // Atomic clear-and-return so React 19 StrictMode's double-mount can't
    // replay twice — the second invocation reads `null`.
    set({ pendingHandoffReplay: null });
    return replay.utterance;
  },

  // Toggle a suggestion in / out of the active visit's selections. We
  // operate on the LAST visit in the array (which is always the active
  // one, because each loop pass appends a new visit). If no visit exists
  // yet for the current step (first time the consultant clicks Pick on
  // this pass), we create one inferring the label from collected.category
  // when present.
  toggleWorkflowSelection: (suggestion) =>
    set((state) => {
      const ws = state.workflowSession;
      if (!ws || ws.phase !== 'step-active') return state;
      const cs = state.commandSession;
      const inferredLabel =
        cs && typeof cs.collected.category === 'string'
          ? (cs.collected.category as string)
          : null;
      const visits = [...ws.visits];
      // The last visit is "current" only if it matches the active step
      // index; otherwise we're picking for the first time on this step.
      const last = visits.at(-1);
      const currentIdx =
        last && last.stepIndex === ws.activeStepIndex ? visits.length - 1 : -1;
      if (currentIdx < 0) {
        // First pick on this step / loop pass — start a new visit.
        visits.push({
          stepIndex: ws.activeStepIndex,
          label: inferredLabel,
          selections: [suggestion],
        });
        return { workflowSession: { ...ws, visits } };
      }
      const current = visits[currentIdx];
      if (!current) return state;
      const already = current.selections.find((s) => s.id === suggestion.id);
      const nextSelections = already
        ? current.selections.filter((s) => s.id !== suggestion.id)
        : [...current.selections, suggestion];
      // If a toggle removes the last pick AND the visit's label was the
      // default category-based label, drop the empty visit so the trip
      // summary doesn't render an empty row.
      if (nextSelections.length === 0) {
        visits.splice(currentIdx, 1);
      } else {
        visits[currentIdx] = { ...current, selections: nextSelections };
      }
      return { workflowSession: { ...ws, visits } };
    }),

  // Reset the active step to phase=collecting with the runner-recomputed
  // seed/question. The workflow's catalog index doesn't change — we're
  // re-running the same step. Selections from prior visits stay intact.
  restartCurrentWorkflowStep: (seedCollected, nextQuestion, missing) =>
    set((state) => {
      const ws = state.workflowSession;
      if (!ws) return state;
      const activeStep = ws.steps[ws.activeStepIndex];
      if (!activeStep) return state;
      return {
        commandSession: {
          command: activeStep.command,
          collected: { ...seedCollected },
          missing,
          phase: 'collecting',
          history: nextQuestion
            ? [{ role: 'assistant', content: nextQuestion }]
            : [],
          pendingQuestion: nextQuestion || null,
          pendingInputWidget: null,
          confirmSummary: null,
          result: null,
          error: null,
        },
      };
    }),
}));
